import { useMutateProcessUploadedDocumentPublic } from '@/hooks/mutation/useMutateProcessUploadedDocumentPublic';
import type { ProcessedFile } from '@/types/processed-file.types';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';

// Isolated atom for onboarding — separate from template creation record.
export const onboardingFilesAtom = atom<ProcessedFile[]>([]);

export const MAX_FILES = 5;

const newUid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `file-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useOnboardingFiles = () => {
  const [files, setFiles] = useAtom(onboardingFilesAtom);
  const { mutateAsync: uploadFile } = useMutateProcessUploadedDocumentPublic();

  const handleFilesAccepted = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;

      const existingKeys = new Set(files.map((f) => `${f.name}-${f.size}`));
      const newFiles = incoming.filter((f) => !existingKeys.has(`${f.name}-${f.size}`));

      if (newFiles.length < incoming.length) {
        toast({
          title: 'Duplicate file',
          description: 'This file has already been uploaded.',
          tone: 'destructive',
        });
        if (newFiles.length === 0) return;
      }

      if (files.length + newFiles.length > MAX_FILES) {
        toast({
          title: 'Maximum file limit reached',
          description: `You can only upload up to ${MAX_FILES} attachments.`,
          tone: 'destructive',
        });
        return;
      }

      const placeholders: ProcessedFile[] = newFiles.map((f) => ({
        uid: newUid(),
        name: f.name,
        size: f.size,
        status: 'uploading',
      }));
      setFiles((prev) => [...prev, ...placeholders]);

      newFiles.forEach((file, i) => {
        const uid = placeholders[i]!.uid;
        void (async () => {
          try {
            const result = await uploadFile([file]);
            setFiles((prev) =>
              prev.map((f) =>
                f.uid === uid
                  ? { ...f, processedContent: result.file_contents, status: 'done' }
                  : f
              )
            );
          } catch (error) {
            logger.error('Failed to process file:', error);
            setFiles((prev) =>
              prev.map((f) => (f.uid === uid ? { ...f, status: 'error' } : f))
            );
          }
        })();
      });
    },
    [files, setFiles, uploadFile]
  );

  const handleRemove = useCallback(
    (file: ProcessedFile) => {
      setFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    },
    [setFiles]
  );

  const resetFiles = useCallback(() => {
    setFiles([]);
  }, [setFiles]);

  const hasPendingUploads = files.some((file) => file.status === 'uploading');

  return { files, handleFilesAccepted, handleRemove, resetFiles, hasPendingUploads };
};
